import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Button from './ui/Button';
import { reportError } from '../lib/errorReporter';

interface Props {
  children: React.ReactNode;
  /** Shown instead of the default screen. Receives a reset callback. */
  fallback?: (args: { error: Error; reset: () => void }) => React.ReactNode;
  /** Identifies where in the tree this boundary sits, so reports say which part broke. */
  boundary?: string;
}

interface State {
  error: Error | null;
}

/**
 * Stops a render error taking the whole app down to a white screen, and
 * reports it.
 *
 * React unmounts the entire tree when a render throws and nothing catches it.
 * Without this the user gets a blank page with no explanation and no way back
 * short of knowing to reload — and we get no report at all, because a white
 * screen makes no network request.
 *
 * Note this catches RENDER errors only. Errors thrown in event handlers,
 * promises and timers never reach a boundary; Sentry's global handlers pick
 * those up separately, which is the other half of why init runs at startup
 * rather than here.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportError(error, 'render');

    // Also to the console, always. In dev this is the only place anyone looks,
    // and in production it costs nothing while making a screen-share with a
    // customer immediately useful.
    console.error('[ErrorBoundary]', this.props.boundary ?? 'app', error, errorInfo.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4 py-10">
        <div className="w-full max-w-md bg-white border border-neutral-200 rounded-xl shadow-sm p-6 text-center">
          <div className="mx-auto h-11 w-11 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>

          <h1 className="text-lg font-semibold text-neutral-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
            This page hit an error and stopped. It has been reported automatically — you
            do not need to send anything.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
            <Button variant="primary" size="md" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload
            </Button>
            <Button variant="secondary" size="md" onClick={this.reset}>
              Try again
            </Button>
          </div>

          {/* The version is what turns "it broke" into something searchable
              when somebody does phone up: reports are grouped by fingerprint
              server-side and stamped with the release they came from. */}
          <p className="mt-5 text-xs text-neutral-400">
            Version <span className="font-mono text-neutral-500">{__APP_VERSION__}</span>
          </p>

          {import.meta.env.DEV && (
            <pre className="mt-4 text-left text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words">
              {error.stack || error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
